import React from 'react'
import PropTypes from 'prop-types'
import { InputNumber, Radio, Tooltip, Table, Icon, Button } from 'antd'

import UploadSession from './UploadSession'

import { withOrderFormContext } from '../../contexts'
import { SwalConfirm } from '../../utils/sweetalert'
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons'
import classNames from 'classnames'

const UPLOADED_DISPLAY_TYPE = {
  LIST: 'LIST',
  GRID: 'GRID',
}

const UPLOADED_DISPLAY_TYPE_OPTIONS = [
  { value: UPLOADED_DISPLAY_TYPE.GRID, icon: <AppstoreOutlined />, label: 'Grid', },
  { value: UPLOADED_DISPLAY_TYPE.LIST, icon: <UnorderedListOutlined />, label: 'List', },
]

const UploadedImages = ({
  images,
  setImages,
  frameSize,
  updateCallback,
  updateRemovedImage,
  updateImageQuantity,
  selectedShape,
  maxQuantity,
  isExtendedUploaderArea
}) => {
  /** @type {boolean} */
  const disabledQuantity = React.useMemo(() => (maxQuantity !== null) && images.length >= maxQuantity, [maxQuantity, images.length])

  // Display type state
  const [displayType, setDisplayType] = React.useState(UPLOADED_DISPLAY_TYPE.GRID)

  const onRemoveImage = async (session) => {
    const { value: confirm } = await SwalConfirm()

    if (confirm) {
      setImages(images.filter(img => img.session !== session))
      updateRemovedImage(session)
    }
  }

  const onRotateImage = (sessionId) => {
    const session = images.find(ss => ss.session === sessionId)

    if (session.orientation === 'square') {
      return
    }

    const orientation = session.orientation === 'portrait'
      ? 'landscape'
      : 'portrait'

    setImages(images.map(image => {
      return image.session === sessionId
        ? { ...image, orientation }
        : image
    }))
  }

  const onAmountChange = (amount, { session }) => {
    if (!!amount && Number.isInteger(amount)) {
      setImages(images.map(image => {
        return image.session === session
          ? { ...image, amount }
          : image
      }))

      updateImageQuantity(amount, session)
    }
  }

  /**
   * The columns of image list
   */
  const tableColumns = React.useMemo(() => [
    {
      title: 'Image',
      key: 'image',
      className: 'align-top w-1/6',
      render: (_, record) => (
        <Tooltip
          placement="right"
          title={<img
            src={record.thumbnail.props.public_url}
            alt={record.original.props.file_name}/>}>
          <img
            src={record.thumbnail.props.public_url}
            alt={record.original.props.file_name}/>
        </Tooltip>
      )
    }, {
      title: 'File name',
      key: 'name',
      className: 'align-top',
      render: (_, record) => (
        <Tooltip placement="bottom" title={record.original.props.file_name}>
          <div style={{ maxWidth: '8rem' }} className='truncate' title={record.original.props.file_name}>
            {record.original.props.file_name}
          </div>
        </Tooltip>
      ),
    }, {
      title: 'Image size',
      key: 'size',
      className: 'align-top',
      render: (_, record) => (
        <div>
          <div className='whitespace-nowrap'>
            {record.original.props.image_sizes[0]} x {record.original.props.image_sizes[1]}
          </div>
        </div>
      )
    },
    {
      title: 'Quantity',
      key: 'quantity',
      className: 'align-top',
      render: (_, record) => (
        <InputNumber
          min={1}
          {...(maxQuantity !== null ? { max: maxQuantity } : {})}
          value={record.amount}
          onChange={(value) => onAmountChange(value, record)}
          disabled={(selectedShape === 'COLLECTION') || disabledQuantity}
        />
      )
    },
    {
      title: '',
      key: 'action',
      className: 'align-top text-end',
      render: (_, record) => (
        <Button
          ghost
          type="danger"
          title='Remove this image'
          onClick={() => onRemoveImage(record.session)}
          className='flex items-center justify-center w-8 h-8'
        >
          <Icon type="delete" className='text-red-500' />
        </Button>
      )
    }
  ], [onAmountChange])

  return (
    <React.Fragment>
      <div className="UploadSessionContainer">
        {/* region radio group */}
        {!!images.length && (
          <Radio.Group className='mb-3' value={displayType} onChange={e => setDisplayType(e.target.value)} >
            { UPLOADED_DISPLAY_TYPE_OPTIONS.map(({ icon, label, value }, index) => (
              <Radio.Button key={index} value={value}>
                <Tooltip placement="bottom" title={label}>
                  <div className='min-h-full flex items-center'>{icon}</div>
                </Tooltip>
              </Radio.Button>
            ))}
          </Radio.Group>
        )}
        {/* endregion radio group */}

        <div>
          {/* region grid display type */}
          {displayType === UPLOADED_DISPLAY_TYPE.GRID && (
            <div className={classNames('flex flex-wrap gap-y-10')}>
              {images.map((image, index) => (
                <div className={classNames(
                  isExtendedUploaderArea ? 'w-1/3' : 'w-1/2',
                )} key={`grid-image-${index}`}>
                  <div className='mr-3'>
                    <UploadSession
                      {...image}
                      croppedArea={image.croppedArea}
                      frameSize={frameSize}
                      onClickRotate={onRotateImage}
                      onClickRemove={onRemoveImage}
                      updateCallback={updateCallback}
                    />
                    <div className="amount-container">
                      <div className="ant-input-group-wrapper">
                        <div className="ant-input-wrapper ant-input-group">
                          <span className="ant-input-group-addon">Quantity</span>
                          <InputNumber
                            min={1}
                            {...(maxQuantity !== null ? { max: maxQuantity } : {})}
                            value={image.amount}
                            onChange={(value) => onAmountChange(value, image)}
                            className="amount-input"
                            disabled={(selectedShape === 'COLLECTION') || disabledQuantity}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* endregion grid display type */}

          {/* region list display type */}
          {displayType === UPLOADED_DISPLAY_TYPE.LIST && (
            <Table
              pagination={false}
              dataSource={images.map(o => ({ ...o, key: `list-image-${o.session}` }))}
              columns={tableColumns}
              scroll={{ x: 'auto' }}
            />
          )}
          {/* endregion list display type */}
        </div>
      </div>
    </React.Fragment>
  )
}

UploadedImages.propTypes = {
  images: PropTypes.array,
  frameSize: PropTypes.object,
  setImages: PropTypes.func.isRequired,
  updateCallback: PropTypes.func.isRequired,
  updateRemovedImage: PropTypes.func.isRequired,
  updateImageQuantity: PropTypes.func.isRequired,
  selectedShape: PropTypes.any,
  maxQuantity: PropTypes.number,
  isExtendedUploaderArea: PropTypes.bool,
}

export default withOrderFormContext(context => ({
  images: context.images,
  frameSize: context.frameSize,
  setImages: context.setImages,
  updateCallback: context.updateUploadedImage,
  updateRemovedImage: context.updateRemovedImage,
  isAspectRatioCorrect: context.isAspectRatioCorrect,
  updateImageQuantity: context.updateImageQuantity,
  selectedShape: context.selectedShape,
  maxQuantity: context.maxQuantity,
  isExtendedUploaderArea: context.isExtendedUploaderArea,
}))(UploadedImages)
